import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Rating,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { FeedbackService } from '../../services/feedbackService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistance } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Composant pour afficher la liste des feedbacks soumis par un demandeur
 */
const RequesterFeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        setLoading(true);
        setError('');
        
        if (!currentUser?.uid) {
          setError('Utilisateur non connecté');
          setLoading(false);
          return;
        }
        
        const userFeedbacks = await FeedbackService.getUserFeedbacks(currentUser.uid);
        setFeedbacks(userFeedbacks);
      } catch (error) {
        console.error('Erreur lors de la récupération des feedbacks:', error);
        setError('Erreur lors de la récupération des feedbacks');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeedbacks();
  }, [currentUser]);
  
  const handleOpenDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const getCriteriaLabel = (value) => {
    switch (value) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Bon';
      case 'average': return 'Moyen';
      case 'poor': return 'À améliorer';
      default: return 'Non évalué';
    }
  };
  
  const getCriteriaColor = (value) => {
    switch (value) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'average': return 'warning';
      case 'poor': return 'error';
      default: return 'default';
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }
  
  if (feedbacks.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Vous n'avez pas encore soumis d'évaluations.
      </Alert>
    );
  }
  
  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Mes évaluations soumises
        </Typography>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Demande</TableCell>
                <TableCell>Service technique</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Détails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbacks.map((feedback) => (
                <TableRow key={feedback.id} hover>
                  <TableCell>
                    {feedback.createdAt ? formatDistance(feedback.createdAt, new Date(), { 
                      addSuffix: true,
                      locale: fr 
                    }) : 'Date inconnue'}
                  </TableCell>
                  <TableCell>{feedback.workRequestTitle || 'Demande sans titre'}</TableCell>
                  <TableCell>{feedback.technicalServiceName || 'Service non spécifié'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={feedback.rating} readOnly size="small" />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        ({feedback.rating}/5)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleOpenDialog(feedback)}
                    >
                      Voir détails
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Dialog pour afficher les détails du feedback */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selectedFeedback && (
          <>
            <DialogTitle>
              Détails de l'évaluation
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Demande: {selectedFeedback.workRequestTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Service technique: {selectedFeedback.technicalServiceName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Soumis {selectedFeedback.createdAt ? formatDistance(selectedFeedback.createdAt, new Date(), { 
                    addSuffix: true,
                    locale: fr 
                  }) : 'à une date inconnue'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Note globale
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating value={selectedFeedback.rating} readOnly />
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    {selectedFeedback.rating}/5
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Critères spécifiques
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip 
                    label={`Respect des délais: ${getCriteriaLabel(selectedFeedback.timeliness)}`}
                    color={getCriteriaColor(selectedFeedback.timeliness)}
                    size="small"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Qualité: ${getCriteriaLabel(selectedFeedback.quality)}`}
                    color={getCriteriaColor(selectedFeedback.quality)}
                    size="small"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Communication: ${getCriteriaLabel(selectedFeedback.communication)}`}
                    color={getCriteriaColor(selectedFeedback.communication)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              
              {selectedFeedback.comment && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Commentaire
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2">
                      {selectedFeedback.comment}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default RequesterFeedbackList;
